import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const AuctionCalendar = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('upcoming');

  const upcomingAuctions = [
    {
      id: 1,
      title: "Heritage Auctions - Sports Collectibles",
      date: "2024-10-05",
      time: "10:00 AM EST",
      category: "Sports Cards",
      estimatedValue: "$2.5M - $3.2M",
      items: 247,
      status: "upcoming",
      auctionHouse: "Heritage Auctions",
      location: "Dallas, TX",
      type: "live",
      featured: true
    },
    {
      id: 2,
      title: "Sotheby\'s Fine Watches",
      date: "2024-10-08",
      time: "2:00 PM EST",
      category: "Vintage Watches",
      estimatedValue: "$5.1M - $7.8M",
      items: 89,
      status: "upcoming",
      auctionHouse: "Sotheby\'s",
      location: "New York, NY",
      type: "live",
      featured: true
    },
    {
      id: 3,
      title: "Comic Connect - Golden Age Comics",
      date: "2024-10-12",
      time: "12:00 PM EST",
      category: "Comic Books",
      estimatedValue: "$1.8M - $2.4M",
      items: 156,
      status: "upcoming",
      auctionHouse: "Comic Connect",
      location: "Online",
      type: "online",
      featured: false
    },
    {
      id: 4,
      title: "Christie\'s Modern Art",
      date: "2024-10-15",
      time: "7:00 PM EST",
      category: "Art & Antiques",
      estimatedValue: "$12.5M - $18.2M",
      items: 67,
      status: "upcoming",
      auctionHouse: "Christie\'s",
      location: "London, UK",
      type: "live",
      featured: true
    }
  ];

  const recentResults = [
    {
      id: 1,
      title: "Bonhams Vintage Timepieces",
      date: "2024-09-28",
      category: "Vintage Watches",
      totalSold: "$4.2M",
      soldRate: "87%",
      topLot: "Rolex Daytona - $485,000",
      performance: "above_estimate"
    },
    {
      id: 2,
      title: "Heritage Sports Platinum",
      date: "2024-09-25",
      category: "Sports Cards",
      totalSold: "$3.8M",
      soldRate: "92%",
      topLot: "1952 Topps Mickey Mantle - $650,000",
      performance: "above_estimate"
    },
    {
      id: 3,
      title: "Comic Art Auction",
      date: "2024-09-22",
      category: "Comic Books",
      totalSold: "$1.9M",
      soldRate: "78%",
      topLot: "Amazing Fantasy #15 - $285,000",
      performance: "within_estimate"
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'text-primary bg-primary/10';
      case 'live': return 'text-conversion-accent bg-conversion-accent/10';
      case 'ended': return 'text-muted-foreground bg-muted/30';
      default: return 'text-muted-foreground bg-muted/30';
    }
  };

  const getPerformanceColor = (performance) => {
    switch (performance) {
      case 'above_estimate': return 'text-success';
      case 'within_estimate': return 'text-primary';
      case 'below_estimate': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  const getPerformanceIcon = (performance) => {
    switch (performance) {
      case 'above_estimate': return 'TrendingUp';
      case 'within_estimate': return 'Minus';
      case 'below_estimate': return 'TrendingDown';
      default: return 'Minus';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        <div>
          <h2 className="text-xl font-bold text-foreground">Auction Calendar</h2>
          <p className="text-sm text-muted-foreground">Track upcoming auctions and recent results</p>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-muted/30 rounded-lg p-1">
            <button
              onClick={() => setViewMode('upcoming')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                viewMode === 'upcoming' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Upcoming
            </button>
            <button
              onClick={() => setViewMode('results')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                viewMode === 'results' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Results
            </button>
          </div>

          {/* Calendar View Button */}
          <button className="flex items-center space-x-2 px-4 py-2 bg-muted/50 text-muted-foreground rounded-lg hover:bg-muted hover:text-foreground transition-colors duration-200">
            <Icon name="Calendar" size={16} />
            <span className="text-sm">Calendar View</span>
          </button>
        </div>
      </div>
      {/* Upcoming Auctions */}
      {viewMode === 'upcoming' && (
        <div className="space-y-4">
          {upcomingAuctions?.map((auction) => (
            <div
              key={auction?.id}
              className={`bg-card border rounded-lg p-6 premium-hover ${
                auction?.featured ? 'border-primary/30 bg-primary/5' : 'border-border'
              }`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-card-foreground">{auction?.title}</h3>
                    {auction?.featured && (
                      <span className="px-2 py-1 bg-primary/20 text-primary text-xs font-medium rounded-full">
                        Featured
                      </span>
                    )}
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      auction?.type === 'live' ? 'bg-conversion-accent/10 text-conversion-accent' : 'bg-muted/30 text-muted-foreground'
                    }`}>
                      {auction?.type === 'live' ? 'Live Auction' : 'Online Only'}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <Icon name="Calendar" size={16} className="text-muted-foreground" />
                      <span className="text-card-foreground">{auction?.date}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Clock" size={16} className="text-muted-foreground" />
                      <span className="text-card-foreground">{auction?.time}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="MapPin" size={16} className="text-muted-foreground" />
                      <span className="text-card-foreground">{auction?.location}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Package" size={16} className="text-muted-foreground" />
                      <span className="text-card-foreground">{auction?.items} items</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col lg:flex-row lg:items-center space-y-3 lg:space-y-0 lg:space-x-6">
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Estimated Value</p>
                    <p className="text-lg font-bold text-card-foreground">{auction?.estimatedValue}</p>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button className="flex items-center space-x-2 px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors duration-200">
                      <Icon name="Bell" size={16} />
                      <span className="text-sm">Set Alert</span>
                    </button>
                    <button className="flex items-center space-x-2 px-4 py-2 bg-muted/50 text-muted-foreground rounded-lg hover:bg-muted hover:text-card-foreground transition-colors duration-200">
                      <Icon name="ExternalLink" size={16} />
                      <span className="text-sm">View</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Recent Results */}
      {viewMode === 'results' && (
        <div className="space-y-4">
          {recentResults?.map((result) => (
            <div
              key={result?.id}
              className="bg-card border border-border rounded-lg p-6 premium-hover"
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-card-foreground">{result?.title}</h3>
                    <span className="px-2 py-1 bg-muted/30 text-muted-foreground text-xs font-medium rounded-full">
                      Completed
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <Icon name="Calendar" size={16} className="text-muted-foreground" />
                      <span className="text-card-foreground">{result?.date}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Tag" size={16} className="text-muted-foreground" />
                      <span className="text-card-foreground">{result?.category}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Award" size={16} className="text-muted-foreground" />
                      <span className="text-card-foreground">{result?.topLot}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col lg:flex-row lg:items-center space-y-3 lg:space-y-0 lg:space-x-6">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Sold</p>
                      <p className="text-lg font-bold text-card-foreground">{result?.totalSold}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Sold Rate</p>
                      <p className="text-lg font-bold text-card-foreground">{result?.soldRate}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Icon 
                      name={getPerformanceIcon(result?.performance)} 
                      size={16} 
                      className={getPerformanceColor(result?.performance)}
                    />
                    <span className={`text-sm font-medium ${getPerformanceColor(result?.performance)}`}>
                      {result?.performance?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AuctionCalendar;